/**
 * Response validation utilities for ThinkMate
 * Centralizes vague response detection logic used across components
 */

const VAGUE_PHRASES = [
  'fine', 'nothing', 'idk', 'ok', 'good', 'alright', 'whatever', 
  '.', '..', '...', 'sure', 'yeah', 'yes', 'no', 'maybe'
];

const UNCERTAIN_PHRASES = [
  'not sure', 'unsure', 'dont know', "don't know", 'unclear', 'confused',
  'not really', 'kinda', 'sorta', 'maybe', 'i guess', 'i think'
];

const BRIEF_RESPONSE_THRESHOLD = 15;

const VAGUE_RESPONSE_THRESHOLD = 3;

/**
 * Detects if a user response is vague or minimal
 * @param response The user's input response
 * @param context Optional context to adjust validation (e.g., 'name' for name questions)
 * @returns true if the response is considered vague
 */
export const isVagueResponse = (response: string, context?: string): boolean => {
  const cleanResponse = response.toLowerCase().trim();
  
  // Check for explicitly vague phrases first
  if (VAGUE_PHRASES.includes(cleanResponse)) {
    return true;
  }
  
  // For name context, be more lenient with short responses
  if (context === 'name') {
    // Names can be short but valid - only flag if it's really minimal
    return cleanResponse.length < 2 || /^[.!?]*$/.test(cleanResponse);
  }
  
  // For other contexts, use the standard threshold
  return cleanResponse.length < VAGUE_RESPONSE_THRESHOLD;
};

/**
 * Detects if a user response indicates uncertainty or confusion
 * @param response The user's input response
 * @returns true if the response indicates uncertainty
 */
export const isUncertainResponse = (response: string): boolean => {
  const cleanResponse = response.toLowerCase().trim();
  
  // Check for uncertain phrases
  for (const phrase of UNCERTAIN_PHRASES) {
    if (cleanResponse.includes(phrase)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Detects if a user response is brief (under threshold but not necessarily vague)
 * @param response The user's input response
 * @returns true if the response is brief
 */
export const isBriefResponse = (response: string): boolean => {
  return response.trim().length < BRIEF_RESPONSE_THRESHOLD;
};

/**
 * Classifies the overall quality/type of a user response
 * @param response The user's input response
 * @returns Classification of response type
 */
export const classifyResponse = (response: string): 'vague' | 'uncertain' | 'brief' | 'detailed' | 'clear' => {
  if (isVagueResponse(response)) return 'vague';
  if (isUncertainResponse(response)) return 'uncertain';
  if (isBriefResponse(response)) return 'brief';
  if (response.trim().length > 50) return 'detailed';
  return 'clear';
};

/**
 * Counts how many responses in an array are vague
 * @param responses Array of user responses
 * @returns Number of vague responses
 */
export const countVagueResponses = (responses: string[]): number => {
  return responses.filter(response => isVagueResponse(response)).length;
};

/**
 * Determines if the majority of responses are vague
 * @param responses Array of user responses
 * @param threshold Minimum number of vague responses to be considered "predominantly vague"
 * @returns true if responses are predominantly vague
 */
export const hasPredominantlyVagueResponses = (responses: string[], threshold: number = 2): boolean => {
  return countVagueResponses(responses) >= threshold;
};